import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  QrCode, ArrowRight, Check, Gift, Camera,
  User, Envelope, Lock, Sparkle, CheckCircle, Warning, Eye, EyeSlash
} from '@phosphor-icons/react';
import GlobalNav from '../../components/GlobalNav';
import { useLanguage } from '../../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Activate = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();
  
  // Steps: 1=Enter Code, 2=Create Account, 3=Success
  const [step, setStep] = useState(1);
  const [code, setCode] = useState(searchParams.get('code') || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  // Card info from validation
  const [cardInfo, setCardInfo] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: ''
  });
  
  // Success data
  const [activationResult, setActivationResult] = useState(null);

  // If code is in URL, auto-validate
  useEffect(() => {
    if (searchParams.get('code')) {
      setCode(searchParams.get('code'));
    }
  }, [searchParams]);

  const steps = [
    { num: 1, label: t('activate_step_1_label') },
    { num: 2, label: t('activate_step_2_label') },
    { num: 3, label: t('activate_step_3_label') }
  ];

  // Validate the invitation code
  const handleValidateCode = async (e) => {
    e.preventDefault();
    if (!code.trim()) {
      setError(t('error_enter_code'));
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/api/invite/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: code.trim() })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || t('error_invalid_code'));
      }
      
      setCardInfo(data);
      setStep(2);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Activate and create account
  const handleActivate = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.email.trim() || !formData.password.trim()) {
      setError(t('error_fill_all_fields'));
      return;
    }
    
    if (formData.password.length < 6) {
      setError(t('error_password_min'));
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/api/invite/activate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          code: code.trim(),
          email: formData.email.trim(),
          password: formData.password,
          name: formData.name.trim(),
          phone: formData.phone.trim() || null
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || t('error_activation_failed'));
      }
      
      setActivationResult(data);
      setStep(3);
      
      // Store user info in localStorage
      localStorage.setItem('habitue-user', JSON.stringify(data.user));
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0F1A14] text-white">
      <GlobalNav />

      {/* Hero */}
      <section className="pt-32 pb-12 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 bg-[#C3A365]/10 border border-[#C3A365]/30 px-4 py-2 rounded-full mb-8"
          >
            <Gift size={16} className="text-[#C3A365]" weight="fill" />
            <span className="text-sm text-[#C3A365] font-medium">{t('activate_badge')}</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl sm:text-5xl font-light tracking-tight mb-6"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            {t('activate_title_1')}<br />
            <span className="text-[#C3A365]">{t('activate_title_2')}</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-[#8BA89A] max-w-xl mx-auto"
          >
            {t('activate_subtitle')}
          </motion.p>
        </div>
      </section>

      {/* Steps indicator */}
      <section className="px-6 pb-8">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-between">
            {steps.map((s, i) => (
              <React.Fragment key={s.num}>
                <div className="flex flex-col items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-colors ${
                    step >= s.num
                      ? 'bg-[#C3A365] border-[#C3A365] text-[#0F1A14]'
                      : 'border-[#283D32] text-[#8BA89A]'
                  }`}>
                    {step > s.num ? (
                      <Check size={18} weight="bold" />
                    ) : (
                      <span className="font-medium">{s.num}</span>
                    )}
                  </div>
                  <p className={`text-xs mt-2 ${step >= s.num ? 'text-white' : 'text-[#8BA89A]'}`}>
                    {s.label}
                  </p>
                </div>
                {i < steps.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-4 ${step > s.num ? 'bg-[#C3A365]' : 'bg-[#283D32]'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="px-6 pb-24">
        <div className="max-w-lg mx-auto">
          <AnimatePresence mode="wait">
            {/* Step 1: Enter Code */}
            {step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="bg-[#18261E] border border-[#283D32] p-6 sm:p-8"
              >
                <h3 className="text-xl font-medium mb-6 text-center">{t('activate_scan_title')}</h3>
                
                {/* QR Scanner placeholder */}
                <div className="aspect-square max-w-xs mx-auto bg-[#1E2F26] border-2 border-dashed border-[#283D32] rounded-xl flex flex-col items-center justify-center mb-6">
                  <Camera size={48} className="text-[#8BA89A] mb-4" />
                  <p className="text-[#8BA89A] text-sm text-center px-4">{t('activate_scan_desc')}</p>
                  <button className="mt-4 px-4 py-2 bg-[#C3A365] text-[#0F1A14] text-sm font-medium">
                    {t('activate_open_camera')}
                  </button>
                </div>

                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-[#283D32]" />
                  </div>
                  <div className="relative flex justify-center">
                    <span className="bg-[#18261E] px-4 text-[#8BA89A] text-sm">{t('activate_or')}</span>
                  </div>
                </div>

                <form onSubmit={handleValidateCode}>
                  <div className="mb-4">
                    <label className="text-sm text-[#8BA89A] block mb-2">{t('activate_enter_code')}</label>
                    <div className="relative">
                      <QrCode size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8BA89A]" />
                      <input
                        type="text"
                        value={code}
                        onChange={(e) => setCode(e.target.value.toUpperCase())}
                        placeholder="HAB-XXXX-XXXX"
                        className="w-full pl-12 pr-4 py-3 bg-[#1E2F26] border border-[#283D32] text-white placeholder:text-[#8BA89A]/50 focus:border-[#C3A365]/50 focus:outline-none uppercase tracking-wider"
                        data-testid="invitation-code-input"
                      />
                    </div>
                  </div>

                  {error && (
                    <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-2">
                      <Warning size={18} className="text-red-400" />
                      <span className="text-red-400 text-sm">{error}</span>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading || code.length < 6}
                    className="w-full py-4 bg-[#C3A365] text-[#0F1A14] font-semibold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="validate-code-btn"
                  >
                    {loading ? (
                      <span className="animate-pulse">{t('loading')}</span>
                    ) : (
                      <>
                        {t('activate_verify')}
                        <ArrowRight size={18} />
                      </>
                    )}
                  </button>
                </form>
              </motion.div>
            )}

            {/* Step 2: Create Account */}
            {step === 2 && cardInfo && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="bg-[#18261E] border border-[#283D32] p-6 sm:p-8"
              >
                <div className="text-center mb-6">
                  <div className="w-16 h-16 rounded-full bg-[#C3A365]/20 flex items-center justify-center mx-auto mb-4">
                    <Gift size={32} className="text-[#C3A365]" />
                  </div>
                  <h3 className="text-xl font-medium mb-2">{t('activate_found_title')}</h3>
                  <p className="text-[#8BA89A]">{t('activate_found_desc')}</p>
                </div>

                {/* Card preview */}
                <div className="bg-[#1E2F26] p-4 rounded-lg mb-6">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-[#8BA89A] text-xs">{t('activate_card_value')}</p>
                      <p className="text-2xl text-white font-light">£{cardInfo.amount?.toFixed(2) || '0.00'}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[#8BA89A] text-xs">{t('activate_from')}</p>
                      <p className="text-white">{cardInfo.sender_name}</p>
                    </div>
                  </div>
                  {cardInfo.message && (
                    <div className="mt-4 pt-4 border-t border-[#283D32]">
                      <p className="text-[#8BA89A] text-xs mb-1">Message</p>
                      <p className="text-white text-sm italic">"{cardInfo.message}"</p>
                    </div>
                  )}
                </div>

                <h4 className="text-lg font-medium mb-4">{t('activate_create_account')}</h4>
                
                <form onSubmit={handleActivate}>
                  <div className="space-y-4 mb-6">
                    <div>
                      <label className="text-sm text-[#8BA89A] block mb-2">{t('activate_name')}</label>
                      <div className="relative">
                        <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8BA89A]" />
                        <input
                          type="text"
                          value={formData.name}
                          onChange={(e) => setFormData({...formData, name: e.target.value})}
                          placeholder="Marie Dupont"
                          className="w-full pl-12 pr-4 py-3 bg-[#1E2F26] border border-[#283D32] text-white placeholder:text-[#8BA89A]/50 focus:border-[#C3A365]/50 focus:outline-none"
                          data-testid="name-input"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="text-sm text-[#8BA89A] block mb-2">{t('activate_email')}</label>
                      <div className="relative">
                        <Envelope size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8BA89A]" />
                        <input
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({...formData, email: e.target.value})}
                          placeholder="marie@email.com"
                          className="w-full pl-12 pr-4 py-3 bg-[#1E2F26] border border-[#283D32] text-white placeholder:text-[#8BA89A]/50 focus:border-[#C3A365]/50 focus:outline-none"
                          data-testid="email-input"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="text-sm text-[#8BA89A] block mb-2">{t('activate_password')}</label>
                      <div className="relative">
                        <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8BA89A]" />
                        <input
                          type={showPassword ? "text" : "password"}
                          value={formData.password}
                          onChange={(e) => setFormData({...formData, password: e.target.value})}
                          placeholder="••••••••"
                          className="w-full pl-12 pr-12 py-3 bg-[#1E2F26] border border-[#283D32] text-white placeholder:text-[#8BA89A]/50 focus:border-[#C3A365]/50 focus:outline-none"
                          data-testid="password-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-[#8BA89A] hover:text-white"
                        >
                          {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                      <p className="text-[#8BA89A] text-xs mt-1">Minimum 6 caractères</p>
                    </div>
                  </div>

                  {error && (
                    <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-2">
                      <Warning size={18} className="text-red-400" />
                      <span className="text-red-400 text-sm">{error}</span>
                    </div>
                  )}

                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => {setStep(1); setError('');}}
                      className="flex-1 py-4 border border-[#283D32] text-white"
                    >
                      {t('gift_back')}
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex-1 py-4 bg-[#C3A365] text-[#0F1A14] font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
                      data-testid="create-account-btn"
                    >
                      {loading ? (
                        <span className="animate-pulse">{t('loading')}</span>
                      ) : (
                        <>
                          {t('activate_create_btn')}
                          <ArrowRight size={18} />
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </motion.div>
            )}

            {/* Step 3: Success */}
            {step === 3 && activationResult && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-[#18261E] border border-[#283D32] p-6 sm:p-8"
              >
                <div className="text-center py-8">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 200 }}
                    className="w-20 h-20 rounded-full bg-[#C3A365]/20 flex items-center justify-center mx-auto mb-6"
                  >
                    <CheckCircle size={48} className="text-[#C3A365]" weight="fill" />
                  </motion.div>

                  <h3 className="text-2xl font-light mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    {t('activate_success_title')}
                  </h3>
                  <p className="text-[#8BA89A] mb-8">
                    {t('activate_success_desc')}
                  </p>

                  {/* Summary */}
                  <div className="bg-[#1E2F26] p-6 rounded-lg mb-8 text-left">
                    <div className="flex justify-between mb-3">
                      <span className="text-[#8BA89A]">{t('activate_card_balance')}</span>
                      <span className="text-white font-medium">£{activationResult.welcome_bonus?.toFixed(2) || '0.00'}</span>
                    </div>
                    <div className="flex justify-between mb-3">
                      <span className="text-[#8BA89A]">{t('activate_points')}</span>
                      <span className="text-[#C3A365] font-medium">0 pts</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#8BA89A]">{t('activate_tier')}</span>
                      <span className="text-white">Member</span>
                    </div>
                  </div>

                  <button
                    onClick={() => navigate('/consumer/dashboard')}
                    className="w-full py-4 bg-[#C3A365] text-[#0F1A14] font-semibold flex items-center justify-center gap-2"
                    data-testid="go-to-wallet-btn"
                  >
                    {t('activate_go_wallet')}
                    <ArrowRight size={18} />
                  </button>

                  <div className="mt-6 flex items-center justify-center gap-2 text-[#8BA89A] text-sm">
                    <Sparkle size={16} className="text-[#C3A365]" />
                    <span>{t('activate_welcome_message')}</span>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* Help section */}
      <section className="px-6 pb-24">
        <div className="max-w-lg mx-auto text-center">
          <p className="text-[#8BA89A] text-sm mb-4">{t('activate_no_card')}</p>
          <button
            onClick={() => navigate('/demander')}
            className="inline-flex items-center gap-2 px-6 py-3 border border-[#283D32] text-white hover:border-[#C3A365]/50 transition-colors"
          >
            {t('request_invitation')}
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-[#1E2F26]">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4">
          <span className="brand-logo text-xl text-[#8BA89A]">habitué</span>
          <p className="text-xs text-[#8BA89A]">© 2025 habitué. {t('footer_rights')}</p>
        </div>
      </footer>
    </div>
  );
};

export default Activate;
